import { Result, IConversation, Err, Ok, Conversation, IConversationMessage, IUser, IGroup, IEvent } from '@/classes'
import { fetchAPI } from '@/utils/useAPI'

export const conversationCreateAPI = async (params: {
	content: string
	targetIds: string[]
}) => {
	const response: Result<{ conversation: IConversation }> = await fetchAPI('conversations/create', params, { method: 'POST' })

	if (!response.ok) return response
	if (!response.data.conversation) return { ok: false, error: 'api-error' } as Err

	return {
		ok: true,
		data: {
			conversation: response.data.conversation
		}
	} as Ok<{
		conversation: Conversation
	}>
}

export const conversationLeaveAPI = async (conversationId: string) => {
	const response: Result<{ conversation: IConversation }> = await fetchAPI('conversations/leave', { conversationId }, { method: 'POST' })

	if (!response.ok) return response
	if (!response.data.conversation) return { ok: false, error: 'api-error' } as Err

	return {
		ok: true,
		data: {
			conversation: response.data.conversation
		}
	} as Ok<{
		conversation: Conversation
	}>
}

export const conversationArchiveAPI = async (conversationId: string, action: 'archive' | 'unarchive') => {
	const response: Result<{ conversation: IConversation }> = await fetchAPI('conversations/archive', { conversationId, action }, { method: 'POST' })

	return response
}

export const conversationReadAPI = async (
	conversationId: string
) => {
	const response: Result<{ conversation: IConversation }> = await fetchAPI('conversations/read', { conversationId }, { method: 'POST' })

	return response
}

export const conversationMessageCreateAPI = async (params: {
	id: string
	_id: string
	conversationId: string
	type?: '' | 'invite'
	userId?: string
	attachments?: {
		gif?: string
		place?: string
		event?: string
		group?: string
	}
	content: string
	parent?: string
	isEdit?: boolean
}) => {
	const response: Result<{ message: IConversationMessage, conversation: IConversation }> = await fetchAPI('conversations/messages/create', params, { method: 'POST' })

	if (!response.ok) return response

	return {
		ok: true,
		data: {
			conversation: response.data.conversation,
			message: response.data.message
		}
	} as Ok<{
		conversation: IConversation
		message: IConversationMessage
	}>
}

export const conversationMessageRemoveAPI = async (
	messageId: string
) => {
	const response: Result<{ success: boolean }> = await fetchAPI('conversations/messages/remove', { messageId }, { method: 'POST' })

	if (!response.ok) return response

	return {
		ok: true,
		data: {
			success: response.data.success
		}
	} as Ok<{
		success: boolean
	}>
}

export const conversationGetAPI = async (params: {
	_id: string
	options?: {
		per_page?: number
		from?: Date
    updatedAt?: Date
	}
}, controller?: AbortController) => {
	const response: Result<{
		conversation: IConversation,
		conversationMessages: IConversationMessage[]
		users: IUser[]
		events: IEvent[]
		groups: IGroup[]
	}> = await fetchAPI('conversations/get', params, { method: 'POST', controller })

	if (!response.ok) return response

	return {
		ok: true,
		data: response.data
	}
}

export const conversationMineGetAPI = async (params: {
	from?: Date
	filter?: '' | 'sent' | 'archived' | 'users' | 'group' | 'event' | 'invited' | 'unread'
	options?: FetchOptions & {
		excludeUsers?: string[]
	}
}, controller?: AbortController) => {
	const response: Result<{
		conversations: IConversation[],
		unreadCount: number,
		requestCount: number,
		usersSuggestedIds: string[] | null,
		count: number,
		users: IUser[],
		groups: IGroup[],
		events: IEvent[]
	}> = await fetchAPI('conversations/list', params, { method: 'POST', controller })

	if (!response.ok) return response

	return response
}

export const conversationUsersSearchAPI = async (
	search: string,
	conversationId: string
) => {
	const response: Result<{ users: IUser[] }> = await fetchAPI('conversations/users/search', { search, conversationId }, { method: 'POST' })

	if (!response.ok) return response

	return response
}

export const conversationJoinAPI = async (
	conversationId: string
) => {
	const response: Result<{ conversation: IConversation }> = await fetchAPI('conversations/join', { conversationId }, { method: 'POST' })

	if (!response.ok) return response

	return {
		ok: true,
		data: {
			conversation: response.data.conversation
		}
	} as Ok<{
		conversation: IConversation
	}>
}

export const conversationConsentAPI = async (
	conversationId: string
) => {
	const response: Result<{ conversation: IConversation }> = await fetchAPI('conversations/consent', { conversationId }, { method: 'POST' })

	if (!response.ok) return response

	return response
}

export const conversationUsersGetAPI = async (
	userIds: string[]
) => {
	const response: Result<{ conversation: IConversation }> = await fetchAPI('conversations/users', { userIds }, { method: 'POST' })

	if (!response.ok) return response

	return response
}

export const conversationMembersGetAPI = async (
	conversationId: string,
	options?: FetchOptions
) => {
	const response: Result<{
		users: IUser[]
	}> = await fetchAPI('conversations/members', { conversationId, options }, { method: 'POST' })

	if (!response.ok) return response

	return response
}
